// react
import { useEffect, useState } from 'react';
import styled from 'styled-components';

// third-party
import FullCalendar from '@fullcalendar/react';
import { DayCellContentArg } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin, { DateClickArg } from '@fullcalendar/interaction';
import jaLocales from '@fullcalendar/core/locales/ja';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

// project import
import { baseColors } from 'utils/constants';
import dayjs from 'dayjs';

const StyleWrapper = styled.div`
  .fc .fc-toolbar.fc-header-toolbar {
    margin-bottom: 0;
  }
  .fc .fc-toolbar-title {
    font-size: 0.75rem;
    font-weight: normal;
    color: #37362f;
  }
  .fc .fc-button-primary {
    font-size: 0.75rem;
    background-color: #ffffff;
    color: #acaba9;
    border: none;
    outline: none;
  }
  .fc .fc-today-button {
    background-color: #ffffff;
    color: #37362f;
    border: none;
    outline: none;
  }
  .fc .fc-button-primary:not(:disabled):active,
  .fc .fc-button-primary:not(:disabled).fc-button-active {
    background-color: #ffffff;
    color: #acaba9;
    box-shadow: none;
  }
  .fc .fc-button-primary:not(:disabled):focus,
  .fc .fc-button-primary:not(:disabled).fc-button-focus {
    background-color: #ffffff;
    color: #acaba9;
    box-shadow: none;
  }
  .fc .fc-today-button:disabled {
    opacity: 1;
  }
  .fc .fc-col-header-cell {
    font-size: 0.75rem;
    font-weight: normal;
    color: #b6b5b3;
    border: none;
  }
  .fc .fc-scrollgrid {
    border-width: 0;
  }
  .fc .fc-scrollgrid-section > * {
    border: none;
  }
  .fc .fc-scrollgrid-sync-table {
    border: 1px;
  }
  .fc .fc-daygrid-day-top {
    flex-direction: row;
    justify-content: center;
  }
  .fc .fc-daygrid-day-number {
    font-size: 0.75rem;
    padding: 2px 2px 0;
  }
  .fc .fc-daygrid-body-unbalanced .fc-daygrid-day-events {
    min-height: 1.5em;
  }
`;

type Props = {
  id?: string;
  label: string;
  value?: string;
  startDate: string;
  endDate: string;
  holidays: string[];
  onChange: (date: string) => Promise<boolean>;
};

type EventType = {
  id: string;
  start: string;
  end: string;
  title: string;
  description: string;
  backgroundColor: string;
  borderColor: string;
  editable: boolean;
};

const defaultEvent = {
  id: 'start_date',
  start: '',
  end: '',
  title: '予定日',
  description: '',
  backgroundColor: baseColors.orange,
  borderColor: baseColors.orange,
  editable: false
};

const Calendar = (props: Props) => {
  const { label, value, startDate, endDate, holidays, onChange } = props;
  const [events, setEvents] = useState<EventType[]>([]);

  const renderDayCell = (e: DayCellContentArg) => {
    const { dayNumberText } = e;
    return dayNumberText.replace('日', '');
  };

  const handleDateClick = async (arg: DateClickArg) => {
    if (await onChange(arg.dateStr)) {
      setEvents([{ ...defaultEvent, start: arg.dateStr, title: label }]);
    }
  };

  const calendarOptions = {
    initialView: 'dayGridMonth',
    plugins: [dayGridPlugin, interactionPlugin],
    locale: 'ja',
    locales: [jaLocales],
    validRange: { start: startDate, end: endDate },
    dayCellContent: renderDayCell,
    dayCellDidMount: (info: any) => {
      dayjs.extend(utc);
      dayjs.extend(timezone);
      const jstDate = dayjs(info.date).tz('Asia/Tokyo'); // JSTに変換
      const cellDate = jstDate.format('YYYY-MM-DD'); // yyyy-MM-dd 形式に変換
      if (holidays.includes(cellDate)) {
        info.el.style.backgroundColor = '#f0f0f0'; // グレーにする
      }
    },
    timeZone: 'local',
    headerToolbar: {
      left: 'prev',
      center: 'title',
      right: 'next'
    },
    events,
    dateClick: handleDateClick
  };

  useEffect(() => {
    if (value) {
      onChange(value);
      setEvents([{ ...defaultEvent, start: value, title: label }]);
    } else {
      setEvents([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [label, value]);

  return (
    <StyleWrapper>
      <FullCalendar {...calendarOptions} />
    </StyleWrapper>
  );
};

export default Calendar;
